import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import axios from 'axios'
import fileDownload from 'js-file-download'

import { Label } from '../../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import LtsRegistrationForm from '../ReportTypeForms/LtsRegistrationForm'
import MonthlyLtsForm from '../ReportTypeForms/MonthlyLtsForm'
import EnrollmentForm from '../ReportTypeForms/EnrollmentForm'
import FacilityOnlyForm from '../ReportTypeForms/FacilityOnlyForm'
import ClassRosterForm from '../ReportTypeForms/KlassPackageRosterForm'
import KlassPackageRegistrationForm from '../ReportTypeForms/KlassPackageRegistrationForm'
import DailyClassRosterForm from '../ReportTypeForms/DailyClassRosterForm'
import SkillProgressForm from '../ReportTypeForms/SkillProgressForm'
import FinancialTransactionsForm from '../ReportTypeForms/FinancialTransactionsForm'
import ReportPreview from '../ReportPreview/ReportPreview'

const REPORT_OPTIONS = [
  { value: 'lts_registration', label: 'LTS Registration (Legacy)' },
  { value: 'lts_monthly', label: 'Monthly LTS' },
  { value: 'enrollment', label: 'Enrollment' },
  { value: 'daily_class_roster', label: 'All Classes by Day' },
  { value: 'klass_package_roster', label: 'Single Class Roster' },
  { value: 'klass_package_registration', label: 'Class Package Registration' },
  { value: 'skill_progress', label: 'Skill Progress' },
  { value: 'staff_coach', label: 'Staff & Coaches' },
  { value: 'financial_transactions', label: 'Financial Transactions' }
]

const REPORT_DESCRIPTIONS = {
  lts_registration: {
    title: 'LTS Registration Report (Legacy)',
    description:
      'Legacy report covering Learn to Skate registration fees collected through Stripe before January 1, 2026. After that date, parents obtain LTS memberships directly from US Figure Skating, so new registrations will not appear here. Use the Enrollment report for post-2026 registrations.',
    includes: ['Guardian email', 'Student name', 'Program name', 'Registration date', 'Payment status']
  },
  lts_monthly: {
    title: 'Monthly LTS Report',
    description:
      'Find students who need LTS numbers assigned. Use this to identify recently enrolled students missing their US Figure Skating registration.',
    includes: ['Students without LTS numbers', 'Enrollment date', 'Guardian contact info']
  },
  enrollment: {
    title: 'Enrollment & Registration Report',
    description:
      'Roster of registered students with full contact details and mailing addresses. Filter by a date range (e.g. Skate Year Jul 1 - today) for grant reports and annual submissions, or leave blank for current enrollments.',
    includes: ['Student info', 'Guardian contact', 'Mailing address', 'Birthdate', 'LTS number', 'Date range filter']
  },
  skill_progress: {
    title: 'Skill Progress Report',
    description:
      "Track a student's advancement through skill levels over time. See their initial skill level, current level, and all skill marks in between.",
    includes: ['Initial vs current skill level', 'Skill marks timeline', 'Progress dates', 'Who marked skills']
  },
  staff_coach: {
    title: 'Staff & Coaches Report',
    description:
      'Directory of all directors and coaches at your facility with their contact info and class assignments.',
    includes: ['Name & contact info', 'Role (Director/Coach)', 'Assigned classes']
  },
  klass_package_roster: {
    title: 'Single Class Roster',
    description:
      'Roster for a SINGLE specific class. Use this when you need just one class with student info and guardian contacts.',
    includes: ['Class day/time/dates', 'Student name & age', 'Skill level & LTS #', 'Guardian name/email/phone']
  },
  daily_class_roster: {
    title: 'All Classes by Day',
    description:
      'Combined roster of ALL students in ALL classes on selected days. Perfect for printing one roster for all Tuesday classes or all Thursday classes in a single report.',
    includes: ['Student name & age', 'Skill level & LTS #', 'Class day/time', 'Guardian name/email/phone']
  },
  klass_package_registration: {
    title: 'Class Package Registration',
    description:
      'Full list of every student registered for a specific class package. Use this to see who has signed up for a session or program package.',
    includes: ['Student name & age', 'Skill level & LTS #', 'Guardian name/email/phone', 'Package title']
  },
  financial_transactions: {
    title: 'Financial Transactions Report',
    description:
      'End-of-year financial report showing all payment transactions from Stripe. Perfect for accounting, tax preparation, and financial record keeping.',
    includes: [
      'Transaction date',
      'Guardian/parent name',
      'Student name',
      'Session/package title',
      'Amount',
      'Stripe fees (est.)',
      'Refunds'
    ]
  }
}

function ReportGenerationForm({ facilities, apiBasePath, isFacilityScoped = false, demo = false }) {
  const [reportType, setReportType] = useState(demo ? 'enrollment' : 'lts_registration')
  const [isGenerating, setIsGenerating] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [previewData, setPreviewData] = useState(null)
  const { replace: redirect } = useHistory()

  const form = useForm({
    defaultValues: {
      facility_ids: isFacilityScoped && facilities.length > 0 ? [facilities[0]?.id] : [],
      date_from: moment().subtract(1, 'week').format('YYYY-MM-DD'),
      date_to: moment().format('YYYY-MM-DD'),
      days_back: 30,
      file_format: 'csv',
      delivery_method: demo ? 'download' : 'both',
      klass_id: null,
      klass_package_id: null,
      student_id: null,
      days_of_week: []
    }
  })

  async function handlePreview(data) {
    setIsGenerating(true)
    try {
      const response = await axios.get(`${apiBasePath}/preview`, {
        params: {
          report_type: reportType,
          ...data
        }
      })
      setPreviewData(response.data)
      setShowPreview(true)
    } catch (error) {
      redirect({
        state: {
          notification: {
            message: error.response?.data?.error || error.message || 'Failed to generate preview',
            color: 'red'
          }
        }
      })
    } finally {
      setIsGenerating(false)
    }
  }

  async function handleGenerate(data) {
    setIsGenerating(true)
    try {
      const parameters = {
        facility_ids: data.facility_ids,
        date_from: data.date_from,
        date_to: data.date_to,
        days_back: data.days_back,
        delivery_method: demo ? 'download' : data.delivery_method,
        klass_id: data.klass_id,
        klass_package_id: data.klass_package_id,
        student_id: data.student_id,
        days_of_week: data.days_of_week
      }

      if (reportType === 'financial_transactions') {
        parameters.date_from = moment(data.date_from).unix()
        parameters.date_to = moment(data.date_to).endOf('day').unix()
      }

      const response = await axios.post(
        apiBasePath,
        {
          report: {
            report_type: reportType,
            file_format: data.file_format,
            parameters
          }
        },
        demo ? { responseType: 'blob' } : undefined
      )

      if (demo) {
        fileDownload(response.data, `${reportType}.${data.file_format}`)
        setShowPreview(false)
        setPreviewData(null)
        return
      }

      const deliveryMessage =
        data.delivery_method === 'email'
          ? 'You will receive an email when your report is ready.'
          : data.delivery_method === 'download'
          ? 'Your report will appear in the history tab when ready.'
          : 'You will receive an email and the report will appear in the history tab.'

      redirect({
        state: {
          notification: {
            message: `Report generation started. ${deliveryMessage}`,
            color: 'green'
          }
        }
      })

      form.reset()
      setShowPreview(false)
      setPreviewData(null)
    } catch (error) {
      redirect({
        state: {
          notification: {
            message: error.message || 'Failed to generate report',
            color: 'red'
          }
        }
      })
    } finally {
      setIsGenerating(false)
    }
  }

  function handleCancelPreview() {
    setShowPreview(false)
    setPreviewData(null)
  }

  function renderReportForm() {
    const commonProps = {
      form,
      facilities,
      onSubmit: handlePreview,
      isSubmitting: isGenerating,
      isFacilityScoped,
      demo
    }

    switch (reportType) {
      case 'lts_registration':
        return <LtsRegistrationForm {...commonProps} />
      case 'lts_monthly':
        return <MonthlyLtsForm {...commonProps} />
      case 'enrollment':
        return <EnrollmentForm {...commonProps} />
      case 'skill_progress':
        return <SkillProgressForm demo={demo} form={form} onSubmit={handlePreview} isSubmitting={isGenerating} />
      case 'staff_coach':
        return <FacilityOnlyForm {...commonProps} />
      case 'klass_package_roster':
        return <ClassRosterForm demo={demo} form={form} onSubmit={handlePreview} isSubmitting={isGenerating} />
      case 'klass_package_registration':
        return (
          <KlassPackageRegistrationForm demo={demo} form={form} onSubmit={handlePreview} isSubmitting={isGenerating} />
        )
      case 'daily_class_roster':
        return <DailyClassRosterForm demo={demo} form={form} onSubmit={handlePreview} isSubmitting={isGenerating} />
      case 'financial_transactions':
        return <FinancialTransactionsForm {...commonProps} />
      default:
        return null
    }
  }

  if (showPreview && previewData) {
    return (
      <ReportPreview
        reportType={reportType}
        previewData={previewData}
        formData={form.getValues()}
        onConfirm={() => handleGenerate(form.getValues())}
        onCancel={handleCancelPreview}
        isGenerating={isGenerating}
      />
    )
  }

  return (
    <div className="max-w-lg">
      <h2 className="text-2xl font-semibold mb-4">Generate New Report</h2>
      <p className="text-gray-600 mb-6">Select a report type and configure options to generate a new report.</p>

      <div className="space-y-6">
        <div>
          <Label htmlFor="report_type">Report Type</Label>
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select a report type" />
            </SelectTrigger>
            <SelectContent>
              {REPORT_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  disabled={demo && ['lts_registration', 'financial_transactions'].includes(option.value)}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="p-4 bg-purple-50 border border-purple-200 rounded-md">
          <h3 className="font-medium text-purple-900 mb-1">{REPORT_DESCRIPTIONS[reportType].title}</h3>
          <p className="text-sm text-purple-700 mb-3">{REPORT_DESCRIPTIONS[reportType].description}</p>
          <div className="text-xs text-purple-600">
            <span className="font-medium">Includes:</span> {REPORT_DESCRIPTIONS[reportType].includes.join(' • ')}
          </div>
        </div>

        {demo && (
          <p className="text-sm text-gray-600">
            Demo reports download immediately. Email and payment-history reports are unavailable.
          </p>
        )}
        {renderReportForm()}
      </div>
    </div>
  )
}

ReportGenerationForm.propTypes = {
  facilities: PropTypes.array.isRequired,
  apiBasePath: PropTypes.string.isRequired,
  demo: PropTypes.bool,
  isFacilityScoped: PropTypes.bool
}

export default ReportGenerationForm
